"use client"

import { useState, useEffect } from "react"
import { Plus, Trash2, Edit2, Loader2, Database, Eye, EyeOff, Save, X, ChevronDown, ChevronUp, Clock } from "lucide-react"
import { collection, addDoc, getDocs, deleteDoc, updateDoc, doc, serverTimestamp, query, orderBy } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { toast } from "sonner"
import { ensureUniqueSlug } from "@/lib/slugs"

export default function QuizzesManager() {
  const [quizzes, setQuizzes] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const [viewState, setViewState] = useState<"list" | "edit">("list")
  const [editingId, setEditingId] = useState<string | null>(null)

  // Form State
  const [title, setTitle] = useState("")
  const [slug, setSlug] = useState("")
  const [duration, setDuration] = useState("10")
  const [description, setDescription] = useState("")
  const [questions, setQuestions] = useState<any[]>([
    { questionText: "", options: ["", "", "", ""], correctOptionIndex: 0, explanation: "" }
  ])

  const fetchQuizzes = async () => {
    setIsLoading(true)
    try {
      const q = query(collection(db, "quizzes"), orderBy("createdAt", "desc"))
      const querySnapshot = await getDocs(q)
      setQuizzes(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
    } catch (error) {
      console.error(error)
      toast.error("Failed to load quizzes.")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchQuizzes()
  }, [])

  const resetForm = () => {
    setTitle("")
    setSlug("")
    setDuration("10")
    setDescription("")
    setQuestions([{ questionText: "", options: ["", "", "", ""], correctOptionIndex: 0, explanation: "" }])
    setEditingId(null)
    setViewState("list")
  }

  const handleEdit = (quiz: any) => {
    setTitle(quiz.title || "")
    setSlug(quiz.slug || "")
    setDuration(quiz.duration?.toString() || "10")
    setDescription(quiz.description || "")
    setQuestions(quiz.questions || [])
    setEditingId(quiz.id)
    setViewState("edit")
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this quiz?")) return
    try {
      await deleteDoc(doc(db, "quizzes", id))
      toast.success("Quiz deleted.")
      fetchQuizzes()
    } catch (error) {
      toast.error("Failed to delete quiz.")
    }
  }

  const addQuestion = () => {
    setQuestions([...questions, { questionText: "", options: ["", "", "", ""], correctOptionIndex: 0, explanation: "" }])
  }

  const updateQuestion = (index: number, field: string, value: any) => {
    const updated = [...questions]
    updated[index][field] = value
    setQuestions(updated)
  }

  const updateOption = (qIndex: number, optIndex: number, value: string) => {
    const updated = [...questions]
    updated[qIndex].options[optIndex] = value
    setQuestions(updated)
  }

  const removeQuestion = (index: number) => {
    const updated = questions.filter((_, i) => i !== index)
    setQuestions(updated)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Generate or validate slug
      const finalSlug = await ensureUniqueSlug("quizzes", slug || title, editingId || undefined)

      const payload = {
        title,
        slug: finalSlug,
        duration: parseInt(duration) || 10,
        description,
        questions,
        isVisible: true
      }

      if (editingId) {
        const existing = quizzes.find(q => q.id === editingId)
        await updateDoc(doc(db, "quizzes", editingId), {
           ...payload,
           isVisible: existing?.isVisible !== undefined ? existing.isVisible : true
        })
        toast.success("Quiz updated.")
      } else {
        await addDoc(collection(db, "quizzes"), {
          ...payload,
          createdAt: serverTimestamp()
        })
        toast.success("Quiz created.")
      }
      resetForm()
      fetchQuizzes()
    } catch (error) {
      console.error(error)
      toast.error("Failed to save quiz.")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (viewState === "list") {
    return (
      <div className="bg-white border border-slate-200 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.05)] w-full">
        <div className="p-4 sm:p-8 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-xl sm:text-2xl font-serif font-medium text-slate-950">Quiz Registry</h3>
          <button onClick={() => { resetForm(); setViewState("edit"); }} className="bg-slate-950 text-white px-4 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-[#b48c36] transition-colors flex items-center gap-2">
            <Plus size={14} /> New Quiz
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Quiz Title</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Questions</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">Duration</th>
                <th className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-8 py-12 text-center">
                    <Loader2 className="animate-spin inline-block text-[#b48c36]" />
                  </td>
                </tr>
              ) : quizzes.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-8 py-12 text-center text-slate-400 text-sm">No quizzes registered.</td>
                </tr>
              ) : (
                quizzes.map((quiz) => (
                  <tr key={quiz.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors group">
                    <td className="px-8 py-5">
                      <div>
                        <p className="font-serif font-bold text-slate-950 leading-none mb-1">{quiz.title}</p>
                        <p className="text-[10px] text-slate-400 font-mono">/{quiz.slug || quiz.id}</p>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-100 px-2 py-1 rounded">
                        {quiz.questions?.length || 0} Questions
                      </span>
                    </td>
                    <td className="px-8 py-5 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                       <div className="flex items-center gap-2">
                          <Clock size={12} /> {quiz.duration} Mins
                       </div>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleEdit(quiz)} className="p-2 hover:bg-[#b48c36]/10 text-slate-400 hover:text-[#b48c36] transition-colors rounded">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDelete(quiz.id)} className="p-2 hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors rounded">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border border-slate-200 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.05)] w-full">
      <div className="p-4 sm:p-8 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-xl sm:text-2xl font-serif font-medium text-slate-950">
          {editingId ? "Modify Quiz" : "Craft New Quiz"}
        </h3>
        <button onClick={resetForm} className="text-slate-400 hover:text-slate-950 transition-colors">
          Cancel
        </button>
      </div>

      <form onSubmit={handleSave} className="p-4 sm:p-8 space-y-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Quiz Title</label>
              <input 
                type="text" 
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="e.g. Modern Indian History - Part 1"
                className="w-full bg-slate-50 border border-slate-100 p-4 font-serif text-lg focus:outline-none focus:border-[#b48c36]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">URL Identifier (Slug)</label>
              <input 
                type="text" 
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="e.g. history-quiz-1"
                className="w-full bg-slate-50 border border-slate-100 p-4 font-mono text-sm focus:outline-none focus:border-[#b48c36]"
              />
              <p className="text-[9px] text-slate-400 italic">Leave empty to auto-generate. Used for public sharing links.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Duration (Minutes)</label>
                <input 
                  type="number" 
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  required
                  className="w-full bg-slate-50 border border-slate-100 p-4 text-sm focus:outline-none focus:border-[#b48c36]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Description</label>
              <textarea 
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Briefly describe what this quiz covers..."
                className="w-full bg-slate-50 border border-slate-100 p-4 text-sm focus:outline-none focus:border-[#b48c36] h-32 resize-none"
              />
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Question Set ({questions.length})</label>
              <button 
                type="button"
                onClick={addQuestion}
                className="text-[9px] font-bold uppercase tracking-widest text-[#b48c36] hover:underline"
              >
                + Add Question
              </button>
            </div>

            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-4 no-scrollbar">
              {questions.map((q, qIdx) => (
                <div key={qIdx} className="bg-slate-50 border border-slate-100 p-6 relative group">
                  <button 
                    type="button"
                    onClick={() => removeQuestion(qIdx)}
                    className="absolute top-4 right-4 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <X size={14} />
                  </button>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest">Question {qIdx + 1}</p>
                      <input 
                        type="text"
                        value={q.questionText}
                        onChange={(e) => updateQuestion(qIdx, "questionText", e.target.value)}
                        placeholder="Type your question..."
                        className="w-full bg-white border border-slate-100 p-3 text-sm focus:outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {q.options.map((opt, oIdx) => (
                        <div key={oIdx} className="space-y-1">
                          <div className="flex items-center gap-2">
                            <input 
                              type="radio"
                              name={`correct-${qIdx}`}
                              checked={q.correctOptionIndex === oIdx}
                              onChange={() => updateQuestion(qIdx, "correctOptionIndex", oIdx)}
                              className="accent-[#b48c36]"
                            />
                            <input 
                              type="text"
                              value={opt}
                              onChange={(e) => updateOption(qIdx, oIdx, e.target.value)}
                              placeholder={`Option ${oIdx + 1}`}
                              className={`w-full bg-white border p-2 text-xs focus:outline-none ${q.correctOptionIndex === oIdx ? 'border-emerald-200' : 'border-slate-100'}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-2">
                       <label className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Explanation (Optional)</label>
                       <textarea 
                        value={q.explanation}
                        onChange={(e) => updateQuestion(qIdx, "explanation", e.target.value)}
                        placeholder="Explain why this answer is correct..."
                        className="w-full bg-white border border-slate-100 p-3 text-xs focus:outline-none h-20 resize-none"
                       />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-100 flex justify-end gap-4">
          <button type="button" onClick={resetForm} className="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-slate-950">
            Discard
          </button>
          <button type="submit" disabled={isSubmitting} className="bg-slate-950 text-white px-12 py-4 text-[10px] font-bold uppercase tracking-widest hover:bg-[#b48c36] disabled:opacity-50 flex items-center gap-3">
            {isSubmitting ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            {editingId ? "Update Quiz" : "Publish Quiz"}
          </button>
        </div>
      </form>
    </div>
  )
}
